
import {
  defineComponent, onMounted, Ref, ref, computed, nextTick,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import _ from 'lodash';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '../store/driveStore';
import AppSkeletonCaseView from '@/components/skeletons/AppSkeletonCaseView.vue';
import DriveInput from '../components/UI/DriveInput.vue';
import DriveFiles from '../components/UI/DriveFiles.vue';
import DriveInfo from '../components/UI/DriveInfo.vue';
import DriveLocation from '../components/UI/DriveLocation.vue';
import AppButton from '@/components/UI/AppButton.vue';
import { HIDE_BORDER, SHARE_TYPES } from '@/types/enums';
import AppLabelInput from '@/components/UI/AppLabelInput.vue';
import DriveUiWrapper from '../components/UI/DriveUIWrapper.vue';
import MoveToPopUp from '@/components/BottomPopUp/PopUpTypes/MoveTo/MoveToPopUp.vue';
// eslint-disable-next-line import/no-cycle
import DriveService from '../services/DriveService';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import SkeletonService from '@/services/SkeletonService';
import AppSkeletonCaseEdit from '@/components/skeletons/AppSkeletonCaseEdit.vue';
import {
  ICase, ILabelInput,
} from '@/types/interfaces';
import AppHeader from '@/components/AppHeader.vue';
import AppHeaderUploadProgress from '@/components/AppHeaderUploadProgress.vue';
import DesktopUploadProgress from '@/modules/drive/components/desktop/UI/DesktopUploadProgress.vue';

export default defineComponent({
  components: {
    DesktopUploadProgress,
    DriveInput,
    DriveFiles,
    DriveInfo,
    DriveLocation,
    AppButton,
    AppLabelInput,
    DriveUiWrapper,
    AppSkeletonCaseEdit,
    AppHeader,
    AppSkeletonCaseView,
    MoveToPopUp,
    // AppAlert,
  },
  setup() {
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const route = useRoute();
    const router = useRouter();

    let caseCopy: null | ICase = null;

    const { currentCaseFileSkeletons, currentCasePhotoSkeletons } = SkeletonService.getSkeletonInfo();

    const errorKey = ref('');
    const dataLoading = ref(false);
    const isProgress: Ref<boolean> = ref(false);
    const percent: Ref<number> = ref(0);
    const isMoveToCasePopUp: Ref<boolean> = ref(false);

    const isDoneDisabled = computed(() => (driveStore.caseTitleValidation || driveStore.caseDescriptionValidation
      || driveStore.casePatientNameValidation || driveStore.casePatientIdValidation || !driveStore.bufferCurrentCaseTitle));

    // const root = computed(() => !driveStore.currentCase.folderId);
    // const currentFolder: Ref<IFolder | undefined> = ref(undefined);
    // const isUpperFolderRoot = computed(() => !currentFolder.value?.upperFolderId);

    // const showDots = computed(() => {
    //   if (!isUpperFolderRoot.value && !root.value) {
    //     return true;
    //   }
    //   return false;
    // });

    const showDots = computed(() => driveStore.currentCase?.path && driveStore.currentCase.path.length > 1);

    const currentFolderText = computed(() => {
      if (!driveStore.currentCase?.path?.length) {
        return '';
      }
      return driveStore.currentCase.path[driveStore.currentCase.path.length - 1].name;
      // if (root.value || !currentFolder.value) {
      //   return '';
      // }
      // return currentFolder.value.name;
    });

    function formatDate(date: number | null): string {
      if (!date) {
        return 'No info';
      }
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
      });
    }

    function onUpdateRequest(per: number): void {
      percent.value = per;
    }

    function isNoCaseChanges() {
      const copyCasePayload = {
        id: driveStore.currentCase.id!,
        title: driveStore.bufferCurrentCaseTitle.trim() || driveStore.currentCase.title.trim(),
        description: driveStore.currentCase.description,
        patientId: driveStore.currentCase.patientId,
        patientName: driveStore.currentCase.patientName,
        folderId: driveStore.currentCase.folderId || undefined,
      };

      const currentCasePayload = {
        id: caseCopy!.id!,
        title: caseCopy!.title.trim(),
        description: caseCopy!.description,
        patientId: caseCopy!.patientId,
        patientName: caseCopy!.patientName,
        folderId: caseCopy!.folderId || undefined,
      };

      const copyCaseLabels = caseCopy!.labels.map((lab) => lab.name);
      const caseLabels = mainStore.inputCaseTags.map((tag) => tag.text);

      const isCaseEqual = _.isEqual(copyCasePayload, currentCasePayload);
      const isCaseLabelsEqual = _.isEqual(copyCaseLabels, caseLabels);

      return isCaseEqual && isCaseLabelsEqual;
    }

    function updateCase() {
      if (isDoneDisabled.value) {
        return;
      }

      driveStore.currentCase.filesAmount = driveStore.currentCase.files.length;
      driveStore.currentCase.photosAmount = driveStore.currentCase.photos.length;

      if (isNoCaseChanges()) {
        router.push(`/drive/case/my/${caseCopy!.id}/view/`);
        return;
      }

      isProgress.value = true;

      driveStore.updateCase(onUpdateRequest).then((response) => {
        driveStore.cases.data = [driveStore.currentCase, ...driveStore.cases.data.filter((cas) => cas.id !== driveStore.currentCase.id)];
        router.push(`/drive/case/my/${response.results.data.id}/view/`).then(() => {
          isProgress.value = false;
          percent.value = 0;
        });
      })
        .catch((e) => {
          const errorData = e?.response?.data;
          if (errorData) {
            if (errorData.errorKey === 'CASE_NAME_EXISTS') {
              driveStore.caseTitleValidation = 'Name is already taken';
              errorKey.value = 'Name is already taken';
            } else {
              driveStore.caseTitleValidation = 'Something gone wrong';
              errorKey.value = 'Something gone wrong';
            }
          } else {
            driveStore.caseTitleValidation = 'Something gone wrong';
            errorKey.value = 'Something gone wrong';
          }
          isProgress.value = false;
          percent.value = 0;
        });
    }

    function validateCaseTitle(): void {
      if (!driveStore.bufferCurrentCaseTitle.trim()) {
        driveStore.caseTitleValidation = 'This filed is required';
      } else if (driveStore.bufferCurrentCaseTitle.trim().length > 255) {
        driveStore.caseTitleValidation = 'Case title must be max 255 characters long.';
      } else {
        driveStore.caseTitleValidation = '';
      }
    }

    function validateCaseDescription(): void {
      if (driveStore.currentCase.description.length > 3000) {
        driveStore.caseDescriptionValidation = 'Case description must be max 3000 characters long.';
      } else {
        driveStore.caseDescriptionValidation = '';
      }
    }

    function validatePatientName(): void {
      if (driveStore.currentCase.patientName.length > 255) {
        driveStore.casePatientNameValidation = 'Case title must be max 255 characters long.';
      } else {
        driveStore.casePatientNameValidation = '';
      }
    }

    function validatePatientId(): void {
      if (driveStore.currentCase.patientId.length > 255) {
        driveStore.casePatientIdValidation = 'Case title must be max 255 characters long.';
      } else {
        driveStore.casePatientIdValidation = '';
      }
    }

    // function fetchFolder(folderId: string) {
    //   if (!folderId) {
    //     currentFolder.value = undefined;
    //     return;
    //   }
    //   DriveService.getFolderById(folderId).then((response) => {
    //     if (response.success) {
    //       currentFolder.value = response.results.data;
    //     }
    //   });
    // }

    function updateLocation(folderId: string) {
      dataLoading.value = true;
      DriveService.getCaseById(route.params.id as string).then((response) => {
        driveStore.currentCase.path = response.results.data.path;
        driveStore.currentCase.folderId = response.results.data.folderId;
      }).finally(() => {
        dataLoading.value = false;
      });
    }

    function updateTags() {
      driveStore.currentCase.labels = mainStore.inputCaseTags.map((tags) => ({
        name: tags.text,
        id: tags.id,
      } as ILabelInput));
    }

    function backAction() {
      if (router.options.history.state.back === '/drive/case/create' && driveStore.currentCase.id) {
        router.push(`/drive/case/create?caseId=${driveStore.currentCase!.id}`);
      } else if ((router.options.history.state.back as string)?.startsWith('/drive/case/create?') && driveStore.currentCase.id) {
        router.push(`${router.options.history.state.back}&caseId=${driveStore.currentCase!.id}`);
      } else if (router.options.history.state.back === null && driveStore.currentCase.id) {
        router.push(`/drive/case/my/${driveStore.currentCase!.id}/view`);
      } else {
        driveStore.resetCurrentCase();
        router.back();
      }
    }

    function setFocusOnLabels() {
      const el = document.getElementById('labelsInput');
      if (el) {
        nextTick(() => {
          el.scrollIntoView();
        });
      }
    }

    onMounted(async () => {
      MonitoringService.sendPageMountedSpeed();

      dataLoading.value = true;
      driveStore.getCaseById(route.params.id as string)
        .then((res) => {
          driveStore.currentCase = { ...res };
          caseCopy = { ...res };
          driveStore.bufferCurrentCaseTitle = res.title;
          driveStore.caseTitleValidation = '';
          mainStore.lablesToTagsInput(res.labels);
          // if (driveStore.currentCase.folderId) {
          //   fetchFolder(driveStore.currentCase.folderId);
          // }
        })
        .finally(() => { dataLoading.value = false; });
    });

    return {
      driveStore,
      dataLoading,
      isProgress,
      percent,
      isDoneDisabled,
      isMoveToCasePopUp,
      showDots,
      errorKey,
      currentFolderText,
      currentCaseFileSkeletons,
      currentCasePhotoSkeletons,
      caseCopy,
      validatePatientName,
      validatePatientId,
      formatDate,
      updateCase,
      updateTags,
      validateCaseTitle,
      validateCaseDescription,
      updateLocation,
      backAction,
      HIDE_BORDER,
      SHARE_TYPES,
      setFocusOnLabels,
      mainStore,
    };
  },
});
